import { addBusinessDays, differenceInBusinessDays, getMonth } from 'date-fns'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { type MedicalPlanDisplayItem } from 'components/member_onboarding_v2/medicalPlanTypes'
import { useOnboarding } from 'components/member_onboarding_v2/OnboardingContext'
import { useFilteredPlans } from 'components/member_onboarding_v2/pages/MedicalPlansPage'
import { type InNetworkVerificationStatusType, useInNetworkVerificationStatus } from 'components/member_onboarding_v2/utils/inNetworkStatus'
import { conditionallyLocalizeDate } from 'utils'
import { type MemberOnboardingProvider } from 'utils/web_api.schemas'

import { useProviderInNetworkVerificationModal, type UseProviderInNetworkVerificationModalReturn } from '../ProviderInNetworkVerificationModal/useProviderInNetworkVerificationModal'

export type ProviderInNetworkVerificationContextType = {
  // selected plan ids to verify in network status of
  selectedPlanIds: string[];
  setSelectedPlanIds: React.Dispatch<React.SetStateAction<string[]>>
  // provider npis to verify in network status of
  selectedProviderNpis: number[];
  // list of providers that are in network for all selected plans
  inNetworkProviders: MemberOnboardingProvider[];
  // hydrated medical plans for the selectedPlanIds
  selectedPlans: MedicalPlanDisplayItem[];
  // callback to toggle plans that the user wants to check in network status of
  onToggleSelectedPlan: (planId: string, isSelected: boolean) => void;
  // callback to remove plan from list to check in network status of
  onRemovePlan: (planId: string) => void;
  // callback to toggle providers that the user wants to check in network status of
  onToggleSelectedProvider: (providerNpi: number, isSelected: boolean) => void;
  // determines if the user can add more providers to check
  canAddMoreProviders: boolean;
  // determines if the user can add more plans to check
  canAddMorePlans: boolean;
  // determines if the user can submit task to check
  canSubmit: boolean;
  // determines if the user can kick off the in network verification task
  canKickOffInNetworkVerificationTask: boolean;
  // determines if the user is near the end of their enrollment event
  isNearEnrollmentEnd: boolean;
  launchModal: UseProviderInNetworkVerificationModalReturn['launchModal']
  turnaroundDescription: string;
  verifyingDescription: string;
} & InNetworkVerificationStatusType

export const defaultContextValue: ProviderInNetworkVerificationContextType = {
  selectedPlanIds: [],
  setSelectedPlanIds: () => { },
  selectedProviderNpis: [],
  onToggleSelectedPlan: () => { },
  onRemovePlan: () => { },
  onToggleSelectedProvider: () => { },
  selectedPlans: [],
  inNetworkProviders: [],
  canAddMorePlans: true,
  canAddMoreProviders: true,
  canSubmit: false,
  records: [],
  verifyingInNetworkStatusPlanIds: [],
  verifyingInNetworkStatusProviderNpis: [],
  canKickOffInNetworkVerificationTask: false,
  isNearEnrollmentEnd: false,
  launchModal: () => {},
  turnaroundDescription: 'Standard turnaround is up to 3 business days',
  verifyingDescription: ''
}

// This context manages the in network verification state for providers and plans the user wants to check
export const ProviderInNetworkVerificationContext = createContext<ProviderInNetworkVerificationContextType>(defaultContextValue)

export const useProviderInNetworkVerificationContext = () => useContext(ProviderInNetworkVerificationContext)

export const MAX_PROVIDERS_TO_VERIFY = 5
export const MAX_PLANS_TO_VERIFY = 3
const VERIFICATION_PROCESSING_TIME_DAYS = 4
export const ProviderInNetworkVerificationContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([])
  const [selectedProviderNpis, setSelectedProviderNpis] = useState<number[]>([])
  const { onboardingData, isLoading } = useOnboarding()
  const providers = onboardingData?.provider_preferences
  const { availablePlans } = useFilteredPlans()

  const {
    records,
    verifyingInNetworkStatusPlanIds,
    verifyingInNetworkStatusProviderNpis,
    inNetworkVerificationProgressStatus,
    inNetworkVerificationCreatedAt,
    error,
    refetchInNetworkStatus,
  } = useInNetworkVerificationStatus()
  const isNearEnrollmentEnd = useMemo(() => {
    const endDateString = onboardingData?.enrollment_event?.end_date
    const enrollmentEndDate = endDateString ? new Date(endDateString) : new Date()
    return differenceInBusinessDays(new Date(enrollmentEndDate), new Date()) < VERIFICATION_PROCESSING_TIME_DAYS
  }, [onboardingData?.enrollment_event?.end_date])
  const canKickOffInNetworkVerificationTask = inNetworkVerificationProgressStatus === 'not_started' && !isNearEnrollmentEnd

  const inNetworkProviders = useMemo(() => {
    if (!availablePlans || !selectedPlanIds.length) return []
    return providers?.filter((provider) => {
      return selectedPlanIds.every(planId => {
        const plan = availablePlans.find(p => p.plan.id === planId)
        return plan?.provider_in_network_map?.find((mapProvider) => {
          return mapProvider.npi === provider.npi
        })?.in_network === 'IN_NETWORK'
      })
    }) ?? []
  }, [providers, selectedPlanIds, availablePlans])

  useEffect(() => {
    if (isLoading) return
    if (providers?.length) {
      setSelectedProviderNpis(
        providers.map(p => p.npi)
          .filter(npi => !inNetworkProviders.find(p => p.npi === npi))
          .slice(0, MAX_PROVIDERS_TO_VERIFY)
      )
    }
  }, [providers, inNetworkProviders])

  const onToggleSelectedPlan = useCallback((planId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedPlanIds(prev => {
        if (prev.length >= MAX_PLANS_TO_VERIFY) return prev
        return [...new Set([...prev, planId])]
      })
    } else {
      setSelectedPlanIds(prev => prev.filter(id => id !== planId))
    }
  }, [])
  const onRemovePlan = useCallback((planId: string) => {
    onToggleSelectedPlan(planId, false)
  }, [onToggleSelectedPlan])

  const onToggleSelectedProvider = useCallback((providerNpi: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedProviderNpis(prev => {
        if (prev.length >= MAX_PROVIDERS_TO_VERIFY) return prev
        return [...new Set([...prev, providerNpi])]
      })
    } else {
      setSelectedProviderNpis(prev => prev.filter(id => id !== providerNpi))
    }
  }, [])

  const selectedPlans = useMemo(() => {
    const result: MedicalPlanDisplayItem[] = []
    const planIdsToMatch = verifyingInNetworkStatusPlanIds.length ? verifyingInNetworkStatusPlanIds : selectedPlanIds
    planIdsToMatch.forEach(id => {
      const plan = availablePlans.find(p => p.plan.id === id)
      if (plan) result.push(plan)
    })
    return result
  }, [selectedPlanIds, availablePlans, verifyingInNetworkStatusPlanIds, inNetworkVerificationProgressStatus])

  const { launchModal, modal } = useProviderInNetworkVerificationModal(setSelectedPlanIds)

  const turnaroundDescription = useMemo(() =>
    // Nov/Dec, month is 0 indexed
    [10, 11].includes(getMonth(new Date()))
      ? 'Standard turnaround time is within 3 business days. During holiday weeks, provider availability may be limited, which can result in longer turnaround times.'
      : 'Standard turnaround time is within 3 business days.'
  , [])

  const verifyingDescription = useMemo(() => {
    const planStr = `${verifyingInNetworkStatusPlanIds.length} ${verifyingInNetworkStatusPlanIds.length === 1 ? 'plan' : 'plans'}`
    const endDateDescription = onboardingData?.enrollment_event?.end_date
      ? `
        Don't worry, you still have until ${conditionallyLocalizeDate(new Date(onboardingData.enrollment_event.end_date))} to choose your plans.
        `
      : ''
    if (inNetworkVerificationProgressStatus === 'done') {
      return `We called your providers and asked if they are in-network with ${planStr} that you're considering.`
    }
    return inNetworkVerificationCreatedAt
      ? `
        We're actively calling your providers and asking if they are in-network with the ${planStr} that you're considering.
        We'll get back to you with results by ${conditionallyLocalizeDate(addBusinessDays(inNetworkVerificationCreatedAt, VERIFICATION_PROCESSING_TIME_DAYS))} at the latest.
        ${endDateDescription}
      `
      : `
        We're actively calling your providers and asking if they are in-network with the the ${planStr} that you're considering.
        We'll get back to you with results as soon as we can. ${endDateDescription}
      `
  }, [
    verifyingInNetworkStatusPlanIds,
    onboardingData?.enrollment_event?.end_date,
    inNetworkVerificationCreatedAt,
    inNetworkVerificationProgressStatus,
  ])

  return (
    <ProviderInNetworkVerificationContext.Provider
      value={{
        selectedPlanIds,
        setSelectedPlanIds,
        selectedProviderNpis,
        onToggleSelectedPlan,
        onRemovePlan,
        onToggleSelectedProvider,
        selectedPlans,
        inNetworkProviders,
        canAddMorePlans: selectedPlanIds.length < MAX_PLANS_TO_VERIFY,
        canAddMoreProviders: selectedProviderNpis.length < MAX_PROVIDERS_TO_VERIFY,
        canSubmit: selectedPlanIds.length > 0 && selectedProviderNpis.length > 0,
        records,
        verifyingInNetworkStatusPlanIds,
        verifyingInNetworkStatusProviderNpis,
        inNetworkVerificationProgressStatus,
        inNetworkVerificationCreatedAt,
        refetchInNetworkStatus,
        canKickOffInNetworkVerificationTask,
        isNearEnrollmentEnd,
        error,
        launchModal,
        turnaroundDescription,
        verifyingDescription
      }}
    >
      {children}
      {modal}
    </ProviderInNetworkVerificationContext.Provider>
  )
}
